import FilterSelect from 'common/components/FilterSelect'
import {
  COUNTRIES,
  countryFlagPath,
  detectDefaultCountry,
  resolveCountryName,
} from 'common/constants/countries'
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  Globe,
  Info,
  Lock,
  Search,
  Users,
} from 'lucide-react'
import React, {FC, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from 'setup'

import {toAbsoluteUrl} from '_metronic/helpers'

import {getSchoolSystems} from '../../../../../../setup/redux/Actions/CsvImport/csvImport'
import {getTimetableCountries} from '../../../../../../setup/redux/Actions/Onboarding/getTimetableCountries'
import {getTimetableCurricula} from '../../../../../../setup/redux/Actions/Onboarding/getTimetableCurricula'
import {
  CurriculaCatalog,
  CurriculumOption,
  alpha2For,
  normalizeCurriculaResponse,
} from '../curriculumPrefill'
import {WizardData} from '../index'

interface Props {
  data: WizardData
  updateData: (data: Partial<WizardData>, options?: {prefill?: boolean}) => void
  isDark: boolean
  onNext: () => void
  isNextDisabled: boolean
  /** True until BOTH preferences and GET /onboarding/data have been merged into `data`. */
  isHydrating: boolean
}

/**
 * School-system row from the legacy `csv-import/school-systems` list (name doubles as the id).
 * Only used as the fallback when `timetable/curricula` has nothing for the country.
 */
type SchoolSystem = {system?: string; country?: string}

/** Lines of curriculum pills (the "Other" pill included) shown before "View all", at any width. */
const MAX_PILL_LINES = 2

/** Pill widths measured off-screen, plus the row they have to fit in. */
type PillLayout = {rowWidth: number; gap: number; other: number; widths: Record<string, number>}

/** Lines a left-to-right wrapping row of items of `widths` takes up. */
const countPillLines = (widths: number[], rowWidth: number, gap: number) => {
  let lines = 0
  let used = 0
  widths.forEach((raw) => {
    // A pill wider than the row shrinks to it (its text wraps), so it takes one line on its own.
    const width = Math.min(raw, rowWidth)
    // Half a pixel of slack: measured widths are fractional, the browser rounds.
    if (lines === 0 || used + gap + width > rowWidth + 0.5) {
      lines += 1
      used = width
    } else {
      used += gap + width
    }
  })
  return lines
}

/** Placeholder pill widths while a country's curricula are being fetched. */
const SKELETON_PILL_WIDTHS = [148, 112, 176, 124] as const
/** Keep the skeleton on screen at least this long — a sub-100ms answer otherwise just flickers. */
const MIN_SKELETON_MS = 450

/**
 * Step 1 — "Setup Basics": Country + Curriculum + Timetable Type on one compact
 * page. Country and curriculum feed the recommendation machinery of the later
 * steps: the curriculum picker is country-scoped (GET timetable/curricula) and
 * each pick stores its API code in `manualData.curriculumCodes`, which the
 * prefill of Institution Setup, Academic Structure and Lesson Planning keys off
 * (GET timetable/prefill, see ../curriculumPrefill.ts); the timetable mode keeps
 * its original role.
 */
const Step1Mode: FC<Props> = ({data, updateData, isDark, onNext, isNextDisabled, isHydrating}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const user = useSelector<RootState>(({userReducer}) => userReducer.user) as Record<
    string,
    any
  > | null
  const generalSettings = useSelector<RootState>(
    ({mainReducer}) => mainReducer.generalSettings
  ) as Record<string, any> | null

  // Country-scoped catalog from timetable/curricula; null until fetched or when the
  // country is not in the dataset (then `fetchedSystems`, the legacy list, stands in).
  const [catalog, setCatalog] = useState<CurriculaCatalog | null>(null)
  const [fetchedSystems, setFetchedSystems] = useState<SchoolSystem[]>([])
  const [isLoadingSystems, setIsLoadingSystems] = useState(false)
  const [showAllCurriculums, setShowAllCurriculums] = useState(false)
  const [curriculumSearch, setCurriculumSearch] = useState('')
  const [otherEditing, setOtherEditing] = useState(false)
  const [otherValue, setOtherValue] = useState('')
  const [curriculumAlreadyAdded, setCurriculumAlreadyAdded] = useState(false)
  const otherInputRef = useRef<HTMLInputElement | null>(null)
  const pillRowRef = useRef<HTMLDivElement | null>(null)
  const pillProbeRef = useRef<HTMLDivElement | null>(null)
  const [pillLayout, setPillLayout] = useState<PillLayout | null>(null)
  const [modeHelpOpen, setModeHelpOpen] = useState(false)
  // True until the user picks a country themselves this session — gates the
  // "Detected from your account" note. Starts true so the note also shows for a
  // country restored from preferences or the tenant's application row, not just
  // one this mount detected; only a manual choice may hide it.
  const [countryWasDetected, setCountryWasDetected] = useState(true)
  // GET timetable/countries: the signup form's country list (dropdown source of
  // truth) plus `currentSelected` — the country stored on the tenant's
  // application row. Names only; codes are never persisted client-side.
  const [apiCountryNames, setApiCountryNames] = useState<string[]>([])
  const [apiSelectedCountry, setApiSelectedCountry] = useState<string | null>(null)
  // Flips once the countries request settles (success or failure) — the country
  // prefill must not fall back to browser-locale detection while the tenant's
  // stored country may still be in flight.
  const [countriesResolved, setCountriesResolved] = useState(false)
  // Flips one render AFTER the country prefill applied (or was skipped), i.e.
  // once `data.country` reflects the final default — the curriculum prefill
  // waits on it so the country-ranked pill ordering is in place first.
  const [countrySettled, setCountrySettled] = useState(false)

  const selectedCurriculums = useMemo(
    () => data.manualData?.curriculums ?? [],
    [data.manualData?.curriculums]
  )

  // Fetch the country list + the tenant's stored country once. Only stashes
  // state — the prefill decision lives in the effect below, so a country the
  // user picks while this request is in flight can never be overwritten by a
  // stale closure.
  const countriesFetchRanRef = useRef(false)
  useEffect(() => {
    if (countriesFetchRanRef.current) return
    countriesFetchRanRef.current = true
    dispatch(
      getTimetableCountries(
        (res: any) => {
          const result = res?.result ?? {}
          const names = (Array.isArray(result?.countries) ? result.countries : [])
            .map((c: any) => String(c?.name ?? '').trim())
            .filter(Boolean)
          setApiCountryNames(names)
          const selected = String(result?.currentSelected?.name ?? '').trim()
          setApiSelectedCountry(selected || null)
          setCountriesResolved(true)
        },
        () => setCountriesResolved(true)
      ) as any
    )
  }, [dispatch])

  // Pre-select the country once the API settles: the tenant's stored country
  // first, account fields / browser locale as fallback. A country restored from
  // preferences arrives before this mounts, so `data.country` being set means
  // the default must not run (and no note shows). `countrySettled` is flipped in
  // the same batch as the prefill write, so it becomes observable only once
  // `data.country` holds the final default.
  const countryPrefillRanRef = useRef(false)
  useEffect(() => {
    if (countryPrefillRanRef.current || !countriesResolved) return
    countryPrefillRanRef.current = true
    if (!data.country) {
      const defaultCountry = apiSelectedCountry ?? detectDefaultCountry(user, generalSettings)
      if (defaultCountry) {
        updateData({country: defaultCountry}, {prefill: true})
      }
    }
    setCountrySettled(true)
  }, [apiSelectedCountry, countriesResolved, data.country, generalSettings, updateData, user])

  // Class-wise is the right answer for most schools (it carries the "Recommended"
  // badge), so it starts selected. Seeded as a prefill for the same reason as the
  // country: a mode restored from preferences arrives before this mounts, so
  // `data.mode` being set means the default must not overwrite it.
  const modePrefillRanRef = useRef(false)
  useEffect(() => {
    if (modePrefillRanRef.current || data.mode) return
    modePrefillRanRef.current = true
    updateData({mode: 'class'}, {prefill: true})
  }, [data.mode, updateData])

  // Curriculum options are country-scoped (GET timetable/curricula): the country's
  // own boards first, then the international curricula offered everywhere. Fetched
  // once the country default has settled (so the first list is already the right
  // country's) and again whenever the country changes. If the request fails — the
  // country is not in the dataset — the legacy school-systems list stands in,
  // without codes; those picks still resolve through the old details endpoint.
  // Latest manualData for the selection reconcile below (the effect closes over
  // the country only, so it must not read a stale curriculum list).
  const manualDataRef = useRef(data.manualData)
  useEffect(() => {
    manualDataRef.current = data.manualData
  })
  const legacyFetchRanRef = useRef(false)
  // False until the first catalog landed: only a CHANGE of country reconciles the
  // selection, a restored selection on first load is left alone.
  const hadCatalogRef = useRef(false)
  useEffect(() => {
    if (!countrySettled) return
    let cancelled = false
    const startedAt = Date.now()
    setIsLoadingSystems(true)
    const stopLoading = () => {
      const wait = Math.max(0, MIN_SKELETON_MS - (Date.now() - startedAt))
      window.setTimeout(() => {
        if (!cancelled) setIsLoadingSystems(false)
      }, wait)
    }

    // Country changed: the selection is rebuilt exactly like a first visit to the new
    // country (decision of 9 Sep 2026) — its most likely board is pre-selected and every
    // catalog pick made under the old country is dropped, the international curricula
    // both countries offer included: they were picked against the old country's list,
    // and carrying them over made the old recommendation look like the new one. Only
    // custom names (no code) survive; those are the user's own words, not a
    // recommendation.
    const reconcileSelection = (next: CurriculaCatalog) => {
      const md = manualDataRef.current
      const codes = md?.curriculumCodes ?? {}
      const inCatalog = new Map(next.curricula.map((o) => [o.name, o.code]))
      const current = md?.curriculums ?? []
      const custom = current.filter((name) => !codes[name])
      // The top-ranked common curriculum — the first pill — like on a first visit.
      const first = (next.commonCurricula[0] ?? next.curricula[0])?.name
      const selection = [...(first ? [first] : []), ...custom.filter((name) => name !== first)]
      if (JSON.stringify(selection) === JSON.stringify(current)) return
      const nextCodes: Record<string, string> = {}
      const nextShortCodes: Record<string, string> = {}
      selection.forEach((name) => {
        const code = inCatalog.get(name)
        if (code) nextCodes[name] = code
        const shortCode = next.curricula.find((o) => o.name === name)?.shortCode
        if (shortCode) nextShortCodes[name] = shortCode
      })
      updateData(
        {
          manualData: {
            ...md,
            curriculums: selection,
            curriculumCodes: nextCodes,
            curriculumShortCodes: nextShortCodes,
          },
        },
        {prefill: true}
      )
    }

    dispatch(
      getTimetableCurricula(
        alpha2For(data.country) ?? data.country ?? null,
        (res: any) => {
          if (cancelled) return
          const next = normalizeCurriculaResponse(res)
          setCatalog(next)
          if (hadCatalogRef.current) reconcileSelection(next)
          hadCatalogRef.current = true
          stopLoading()
        },
        () => {
          if (cancelled) return
          setCatalog(null)
          if (legacyFetchRanRef.current) {
            stopLoading()
            return
          }
          legacyFetchRanRef.current = true
          dispatch(
            getSchoolSystems(
              (res: any) => {
                if (cancelled) return
                setFetchedSystems(res?.result ?? [])
                stopLoading()
              },
              () => {
                if (!cancelled) stopLoading()
              }
            ) as any
          )
        }
      ) as any
    )
    return () => {
      cancelled = true
    }
  }, [countrySettled, data.country, dispatch, updateData])

  // Dropdown options come from timetable/countries (the same list the signup
  // form used), so it always contains whatever the tenant has stored; the
  // bundled COUNTRIES list is only the fallback for a failed fetch. Bundled flag
  // SVGs, not the emoji icons — Windows renders flag emoji as bare letter pairs
  // ("PK") instead of flags — and only for names the local flag assets cover.
  const countryOptions = useMemo(() => {
    const names = apiCountryNames.length > 0 ? apiCountryNames : COUNTRIES.map((c) => c.value)
    return names.map((name) => {
      const flag = resolveCountryName(name) ? countryFlagPath(name) : null
      return {value: name, label: name, iconUrl: flag ? toAbsoluteUrl(flag) : undefined}
    })
  }, [apiCountryNames])

  // Curriculum options in "most likely first" order, as the API returns them: the
  // country's boards (the first `nationalCount`), then the international ones. The
  // legacy fallback is shaped the same way — systems tagged with the country first,
  // then the rest — but carries no codes.
  const {options, nationalCount} = useMemo(() => {
    if (catalog && catalog.curricula.length > 0) {
      return {options: catalog.curricula, nationalCount: catalog.nationalCount}
    }
    const needle = (data.country ?? '').trim().toLowerCase()
    const legacy: CurriculumOption[] = fetchedSystems
      .map((s) => ({
        code: '',
        name: String(s?.system ?? '').trim(),
        scope: 'international' as const,
        awardingBody: null,
        country: String(s?.country ?? '').trim() || null,
      }))
      .filter((o) => o.name.length > 0)
    const matches = legacy.filter((o) => needle && (o.country ?? '').toLowerCase() === needle)
    const rest = legacy.filter((o) => !matches.includes(o))
    return {
      options: [...matches.map((o) => ({...o, scope: 'national' as const})), ...rest],
      nationalCount: matches.length,
    }
  }, [catalog, data.country, fetchedSystems])

  const codeByName = useMemo(() => {
    const map = new Map<string, string>()
    options.forEach((o) => {
      if (o.code) map.set(o.name, o.code)
    })
    return map
  }, [options])

  const shortCodeByName = useMemo(() => {
    const map = new Map<string, string>()
    options.forEach((o) => {
      if ('shortCode' in o && o.shortCode) map.set(o.name, o.shortCode)
    })
    return map
  }, [options])

  // Codes for a selection: the catalog's code wins, a code saved earlier (a board
  // picked before the country was changed) is kept, and names without one — typed
  // under "Other", or from the legacy list — simply have none.
  const codesFor = (names: string[]): Record<string, string> => {
    const previous = data.manualData?.curriculumCodes ?? {}
    const next: Record<string, string> = {}
    names.forEach((name) => {
      const code = codeByName.get(name) ?? previous[name]
      if (code) next[name] = code
    })
    return next
  }

  // Short codes ("FBISE") the same way; sent as `curriculum_short_code` on each level.
  const codeFieldsFor = (names: string[]) => {
    const previous = data.manualData?.curriculumShortCodes ?? {}
    const curriculumShortCodes: Record<string, string> = {}
    names.forEach((name) => {
      const shortCode = shortCodeByName.get(name) ?? previous[name]
      if (shortCode) curriculumShortCodes[name] = shortCode
    })
    return {curriculumCodes: codesFor(names), curriculumShortCodes}
  }

  // Names the user typed under "Other". They stay on the row whether selected or not - a
  // custom name is in no catalog, so the selection alone cannot keep it around.
  const customCurriculums = useMemo(
    () => data.manualData?.customCurriculums ?? [],
    [data.manualData?.customCurriculums]
  )

  // Pill candidates, best first: the country's common curricula by `rank`, then the rest of
  // the catalog (local boards first) so the pill lines are always full. The first one is the
  // default selection.
  const rankedNames = useMemo(() => {
    const names = new Set<string>()
    ;[...(catalog?.commonCurricula ?? []), ...options].forEach((o) => {
      const name = o.name.trim()
      if (name) names.add(name)
    })
    return Array.from(names)
  }, [catalog, options])

  // Custom names and selections always get a pill, even when ranked below the cut.
  const pinnedNames = useMemo(
    () => Array.from(new Set([...customCurriculums, ...selectedCurriculums])),
    [customCurriculums, selectedCurriculums]
  )
  const probeNames = useMemo(
    () => Array.from(new Set([...rankedNames, ...pinnedNames])),
    [pinnedNames, rankedNames]
  )

  // Measure every candidate pill (and "Other") in an off-screen probe, and the row they
  // go in. Re-measured when the row resizes (viewport) and when a pill does (fonts loading,
  // a check mark added on select).
  useLayoutEffect(() => {
    const row = pillRowRef.current
    const probe = pillProbeRef.current
    if (!row || !probe) return
    const measure = () => {
      const widths: Record<string, number> = {}
      probe.querySelectorAll<HTMLElement>('[data-pill-name]').forEach((el) => {
        widths[el.dataset.pillName ?? ''] = el.getBoundingClientRect().width
      })
      let other = 0
      probe.querySelectorAll<HTMLElement>('[data-pill-other]').forEach((el) => {
        other = Math.max(other, el.getBoundingClientRect().width)
      })
      const next: PillLayout = {
        rowWidth: row.clientWidth,
        gap: parseFloat(getComputedStyle(row).columnGap) || 0,
        other,
        widths,
      }
      setPillLayout((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    probe.querySelectorAll('[data-pill-name], [data-pill-other]').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [isLoadingSystems, probeNames, selectedCurriculums])

  // As many top-ranked pills as fit in MAX_PILL_LINES next to the pinned ones and "Other",
  // in rank order (custom names last); everything else is under "View all".
  const pillSystems = useMemo(() => {
    const pinned = new Set(pinnedNames)
    const free = rankedNames.filter((name) => !pinned.has(name))
    const extras = pinnedNames.filter((name) => !rankedNames.includes(name))
    const build = (count: number) => {
      const taken = new Set(free.slice(0, count))
      return [...rankedNames.filter((name) => pinned.has(name) || taken.has(name)), ...extras]
    }
    if (!pillLayout) return build(free.length)
    const fits = (names: string[]) =>
      countPillLines(
        [...names.map((name) => pillLayout.widths[name] ?? 0), pillLayout.other],
        pillLayout.rowWidth,
        pillLayout.gap
      ) <= MAX_PILL_LINES
    let best = build(0)
    for (let count = 1; count <= free.length; count += 1) {
      const next = build(count)
      if (!fits(next)) break
      best = next
    }
    return best
  }, [pillLayout, pinnedNames, rankedNames])

  // Pre-select the first recommended pill once the systems arrive, so the
  // step starts answerable without a click. Runs at most once per mount, and only
  // after hydration settles: unlike the mode (restored synchronously before this
  // mounts), saved curriculums land in `data` only when BOTH preferences and
  // GET /onboarding/data resolve — firing earlier would both mistake a resuming
  // user for a fresh one AND spread a stale pre-hydration manualData over the
  // hydrated state. `isHydrating` flips false in the same batch that writes the
  // restored curriculums, so waiting on it closes that race. Also waits for
  // `countrySettled` so the country default is already applied and the pills are
  // country-ranked before the top one is taken.
  const curriculumPrefillRanRef = useRef(false)
  useEffect(() => {
    if (
      curriculumPrefillRanRef.current ||
      isHydrating ||
      !countrySettled ||
      isLoadingSystems ||
      options.length === 0
    ) {
      return
    }
    curriculumPrefillRanRef.current = true
    if (selectedCurriculums.length > 0) return
    const defaults = rankedNames.slice(0, 1)
    if (defaults.length === 0) return
    updateData(
      {
        manualData: {
          ...data.manualData,
          curriculums: defaults,
          ...codeFieldsFor(defaults),
        },
      },
      {prefill: true}
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countrySettled,
    data.manualData,
    options.length,
    isHydrating,
    isLoadingSystems,
    rankedNames,
    selectedCurriculums.length,
    updateData,
  ])

  const filteredOptions = useMemo(() => {
    const term = curriculumSearch.trim().toLowerCase()
    if (!term) return options
    return options.filter(
      (o) =>
        o.name.toLowerCase().includes(term) ||
        (o.awardingBody ?? '').toLowerCase().includes(term) ||
        (o.country ?? '').toLowerCase().includes(term)
    )
  }, [curriculumSearch, options])

  const toggleCurriculum = (system: string) => {
    const current = [...selectedCurriculums]
    const idx = current.indexOf(system)
    if (idx > -1) current.splice(idx, 1)
    else current.push(system)
    updateData({
      manualData: {...data.manualData, curriculums: current, ...codeFieldsFor(current)},
    })
  }

  useEffect(() => {
    if (otherEditing) otherInputRef.current?.focus()
  }, [otherEditing])

  // Same contract as the old step-3 picker's "Other...": free-text names join
  // `curriculums` alongside API systems (deduplicated case-insensitively). A name already on
  // the row (selected or not) is an error — the input stays open on Enter, blur just closes
  // it. A catalog board that is not on the row yet is selected; a genuinely new name is also
  // recorded in `customCurriculums`.
  const confirmOtherCurriculum = (fromBlur = false) => {
    const name = otherValue.trim()
    const same = (other: string) => other.toLowerCase() === name.toLowerCase()
    if (name && (selectedCurriculums.some(same) || pillSystems.some(same))) {
      if (fromBlur) {
        setOtherEditing(false)
        setOtherValue('')
      }
      setCurriculumAlreadyAdded(true)
      return
    }
    setOtherEditing(false)
    setOtherValue('')
    setCurriculumAlreadyAdded(false)
    if (!name) return
    const existing = options.find((o) => same(o.name))?.name
    const resolved = existing ?? name
    const next = [...selectedCurriculums, resolved]
    const alreadyCustom = customCurriculums.some(same)
    updateData({
      manualData: {
        ...data.manualData,
        curriculums: next,
        ...codeFieldsFor(next),
        ...(!existing && !alreadyCustom
          ? {customCurriculums: [...customCurriculums, resolved]}
          : {}),
      },
    })
  }

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    gap: '1.5rem',
    borderRadius: 14,
    padding: '1.5rem',
    border: `1px solid ${isDark ? '#2b2b40' : '#dee2e6'}`,
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#ffffff',
  }
  const iconBubbleStyle: React.CSSProperties = {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#056EE9',
    backgroundColor: isDark ? 'rgba(5,110,233,0.15)' : 'rgba(5,110,233,0.08)',
  }
  const sectionTitleStyle: React.CSSProperties = {
    color: isDark ? '#ffffff' : '#181C32',
    fontSize: '1.05rem',
    fontWeight: 700,
    marginBottom: '0.35rem',
  }
  const sectionDescStyle: React.CSSProperties = {
    color: isDark ? '#92929f' : '#7E8299',
    fontSize: '0.92rem',
    lineHeight: 1.6,
    marginBottom: 0,
  }
  const mutedNoteStyle: React.CSSProperties = {
    color: isDark ? '#7e8299' : '#7E8299',
    fontSize: '0.85rem',
  }

  const modeCardStyle = (option: 'class' | 'subject'): React.CSSProperties => ({
    cursor: 'pointer',
    borderRadius: 12,
    padding: '1.25rem 1.5rem',
    flex: 1,
    position: 'relative',
    textAlign: 'center',
    transition: 'all 0.25s ease',
    border: `2px solid ${data.mode === option ? '#056EE9' : isDark ? '#2b2b40' : '#dee2e6'}`,
    backgroundColor:
      data.mode === option
        ? isDark
          ? 'rgba(0, 149, 232, 0.08)'
          : 'rgba(0, 149, 232, 0.04)'
        : isDark
          ? 'rgba(255,255,255,0.03)'
          : '#ffffff',
  })

  const recommendedLabel =
    (nationalCount > 0 || (catalog?.commonCurricula.length ?? 0) > 0) && data.country
      ? intl.formatMessage(
          {id: 'ONBOARDING_WIZARD.STEP1.CURRICULUM_RECOMMENDED_FOR'},
          {country: data.country}
        )
      : intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CURRICULUM_POPULAR'})

  return (
    <div className='d-flex flex-column step1-basics' style={{gap: '1.25rem', paddingTop: '1.5rem'}}>
      <div>
        <h2
          style={{
            color: isDark ? '#ffffff' : '#181C32',
            fontSize: '1.6rem',
            fontWeight: 700,
            marginBottom: '0.4rem',
          }}
        >
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.HEADER_TITLE'})}
        </h2>
        <p style={{...sectionDescStyle, fontSize: '1rem'}}>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.HEADER_SUBTITLE'})}
        </p>
      </div>

      {/* 1. Country */}
      <div className='step1-basics-section' style={sectionStyle}>
        <div className='step1-basics-info'>
          <div className='d-flex align-items-start gap-3'>
            <div style={iconBubbleStyle}>
              <Globe size={20} />
            </div>
            <div>
              <h6 style={sectionTitleStyle}>
                1. {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_TITLE'})}
              </h6>
              <p style={sectionDescStyle}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_DESC'})}
              </p>
            </div>
          </div>
        </div>
        <div className='step1-basics-body'>
          <label
            style={{...mutedNoteStyle, display: 'block', marginBottom: '0.4rem', fontWeight: 600}}
          >
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_LABEL'})}
          </label>
          <FilterSelect
            value={data.country ?? ''}
            options={countryOptions}
            onChange={(v) => {
              setCountryWasDetected(false)
              updateData({country: v})
            }}
            placement='under'
            searchable
            searchPlaceholder={intl.formatMessage({
              id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_SEARCH_PLACEHOLDER',
            })}
            emptyMessage={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_EMPTY'})}
            className='step1-country-select'
            triggerClassName='step1-country-select-trigger'
            ariaLabel={intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_LABEL'})}
          />
          {!data.country && (
            <div style={{...mutedNoteStyle, marginTop: '0.5rem'}}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_PLACEHOLDER'})}
            </div>
          )}
          {countryWasDetected && data.country && (
            <div className='d-flex align-items-center gap-2' style={{marginTop: '0.6rem'}}>
              <Lock size={13} style={{color: isDark ? '#7e8299' : '#7E8299', flexShrink: 0}} />
              <span style={mutedNoteStyle}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.COUNTRY_DETECTED_NOTE'})}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 2. Curriculum */}
      <div className='step1-basics-section' style={sectionStyle}>
        <div className='step1-basics-info'>
          <div className='d-flex align-items-start gap-3'>
            <div style={iconBubbleStyle}>
              <BookOpen size={20} />
            </div>
            <div>
              <h6 style={sectionTitleStyle}>
                2. {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CURRICULUM_TITLE'})}
              </h6>
              <p style={sectionDescStyle}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CURRICULUM_DESC'})}
              </p>
            </div>
          </div>
        </div>
        <div className='step1-basics-body'>
          <div
            style={{...mutedNoteStyle, fontWeight: 600, color: '#056EE9', marginBottom: '0.6rem'}}
          >
            {isLoadingSystems ? (
              <span className='step1-curriculum-skeleton step1-curriculum-skeleton--label' />
            ) : (
              recommendedLabel
            )}
          </div>
          {isLoadingSystems ? (
            // Country changed: the catalog is being re-fetched, so placeholder pills
            // stand in rather than the previous country's boards.
            <div className='d-flex flex-wrap gap-2' aria-busy='true'>
              {SKELETON_PILL_WIDTHS.map((width, i) => (
                <span
                  key={i}
                  className='step1-curriculum-pill step1-curriculum-skeleton'
                  style={{width}}
                  aria-hidden
                />
              ))}
            </div>
          ) : (
            <div ref={pillRowRef} className='d-flex flex-wrap gap-2'>
              {pillSystems.map((name) => {
                const selected = selectedCurriculums.includes(name)
                return (
                  <button
                    key={name}
                    type='button'
                    className='step1-curriculum-pill'
                    aria-pressed={selected}
                    onClick={() => toggleCurriculum(name)}
                    style={{
                      border: `1.5px solid ${selected ? '#056EE9' : isDark ? '#3f4254' : '#d8dae5'}`,
                      backgroundColor: selected
                        ? isDark
                          ? 'rgba(5,110,233,0.15)'
                          : 'rgba(5,110,233,0.08)'
                        : 'transparent',
                      color: selected ? '#056EE9' : isDark ? '#d1d3e0' : '#5E6278',
                    }}
                  >
                    {selected && <Check size={14} strokeWidth={3} />}
                    {name}
                  </button>
                )
              })}
              {otherEditing ? (
                <input
                  ref={otherInputRef}
                  type='text'
                  className='step1-curriculum-pill step1-curriculum-other-input'
                  value={otherValue}
                  placeholder={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.CURRICULUM.OTHER_PLACEHOLDER',
                  })}
                  onChange={(e) => {
                    setOtherValue(e.target.value)
                    setCurriculumAlreadyAdded(false)
                  }}
                  onBlur={() => confirmOtherCurriculum(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      confirmOtherCurriculum()
                    } else if (e.key === 'Escape') {
                      setOtherEditing(false)
                      setOtherValue('')
                      setCurriculumAlreadyAdded(false)
                    }
                  }}
                  aria-invalid={curriculumAlreadyAdded || undefined}
                  style={{
                    border: `1.5px dashed ${
                      curriculumAlreadyAdded ? '#f1416c' : isDark ? '#3f4254' : '#d8dae5'
                    }`,
                    backgroundColor: 'transparent',
                    color: isDark ? '#ffffff' : '#181C32',
                  }}
                />
              ) : (
                <button
                  type='button'
                  className='step1-curriculum-pill'
                  onClick={() => {
                    setOtherEditing(true)
                    setCurriculumAlreadyAdded(false)
                  }}
                  style={{
                    border: `1.5px dashed ${isDark ? '#3f4254' : '#d8dae5'}`,
                    backgroundColor: 'transparent',
                    color: isDark ? '#d1d3e0' : '#5E6278',
                  }}
                >
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.OTHER'})}
                </button>
              )}
            </div>
          )}
          {!isLoadingSystems && (
            // Off-screen copies of every candidate pill, sized exactly like the real ones, so
            // the row knows how many fit in MAX_PILL_LINES. "Other" counts at its wider state
            // (the input) so opening it does not reflow the row.
            <div ref={pillProbeRef} className='step1-curriculum-probe' aria-hidden inert>
              {probeNames.map((name) => (
                <button
                  key={name}
                  type='button'
                  tabIndex={-1}
                  data-pill-name={name}
                  className='step1-curriculum-pill'
                  style={{border: '1.5px solid transparent'}}
                >
                  {selectedCurriculums.includes(name) && <Check size={14} strokeWidth={3} />}
                  {name}
                </button>
              ))}
              <button
                type='button'
                tabIndex={-1}
                data-pill-other
                className='step1-curriculum-pill'
                style={{border: '1.5px dashed transparent'}}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.OTHER'})}
              </button>
              <input
                type='text'
                tabIndex={-1}
                readOnly
                data-pill-other
                className='step1-curriculum-pill step1-curriculum-other-input'
                placeholder={intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.CURRICULUM.OTHER_PLACEHOLDER',
                })}
                style={{border: '1.5px dashed transparent'}}
              />
            </div>
          )}
          {curriculumAlreadyAdded && (
            <div
              role='alert'
              style={{color: '#f1416c', fontSize: '0.8rem', fontWeight: 600, marginTop: '0.5rem'}}
            >
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CURRICULUM_ALREADY_ADDED'})}
            </div>
          )}
          <div className='d-flex align-items-center gap-2' style={{marginTop: '0.85rem'}}>
            <span style={mutedNoteStyle}>
              {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CURRICULUM_VIEW_ALL_PROMPT'})}
            </span>
            <button
              type='button'
              className='step1-viewall-link'
              onClick={() => setShowAllCurriculums((open) => !open)}
            >
              {intl.formatMessage({
                id: showAllCurriculums
                  ? 'ONBOARDING_WIZARD.STEP1.CURRICULUM_HIDE_ALL'
                  : 'ONBOARDING_WIZARD.STEP1.CURRICULUM_VIEW_ALL',
              })}
              <ChevronRight
                size={14}
                style={{
                  transition: 'transform 0.2s ease',
                  transform: showAllCurriculums ? 'rotate(90deg)' : 'none',
                }}
              />
            </button>
          </div>
          {showAllCurriculums && (
            <div
              className='step1-viewall-panel'
              style={{
                border: `1px solid ${isDark ? '#2b2b40' : '#e4e6ef'}`,
                backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#f9f9fb',
              }}
            >
              <div className='step1-viewall-search'>
                <Search size={15} style={{color: isDark ? '#7e8299' : '#a1a5b7', flexShrink: 0}} />
                <input
                  type='text'
                  value={curriculumSearch}
                  onChange={(e) => setCurriculumSearch(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.CURRICULUM.SEARCH_PLACEHOLDER',
                  })}
                  style={{color: isDark ? '#ffffff' : '#181C32'}}
                />
              </div>
              <div className='step1-viewall-list'>
                {filteredOptions.length === 0 && (
                  <div style={{...mutedNoteStyle, padding: '0.75rem', textAlign: 'center'}}>
                    {intl.formatMessage({id: 'ONBOARDING_WIZARD.CURRICULUM.NO_RESULTS'})}
                  </div>
                )}
                {/* Grouped by scope: the country's boards, then the international curricula
                    (offered everywhere on purpose — Cambridge and the IB run in 150+ countries). */}
                {(['national', 'international'] as const).map((scope) => {
                  const group = filteredOptions.filter((o) => o.scope === scope)
                  if (group.length === 0) return null
                  return (
                    <React.Fragment key={scope}>
                      <div className='step1-viewall-group' style={mutedNoteStyle}>
                        {intl.formatMessage({
                          id:
                            scope === 'national'
                              ? 'ONBOARDING_WIZARD.STEP1.CURRICULUM_LOCAL_BOARDS'
                              : 'ONBOARDING_WIZARD.STEP1.CURRICULUM_INTERNATIONAL',
                        })}
                      </div>
                      {group.map((o) => {
                        const selected = selectedCurriculums.includes(o.name)
                        return (
                          <button
                            key={o.code || o.name}
                            type='button'
                            className={`step1-viewall-option${selected ? ' is-selected' : ''}`}
                            onClick={() => toggleCurriculum(o.name)}
                            style={{color: isDark ? '#d1d3e0' : '#3f4254'}}
                          >
                            <span className='step1-viewall-option-name'>{o.name}</span>
                            {o.awardingBody || o.country ? (
                              <span className='step1-viewall-option-body' style={mutedNoteStyle}>
                                {o.awardingBody ?? o.country}
                              </span>
                            ) : null}
                            {selected && (
                              <Check
                                className='step1-viewall-option-check'
                                size={15}
                                strokeWidth={3}
                                style={{color: '#056EE9'}}
                              />
                            )}
                          </button>
                        )
                      })}
                    </React.Fragment>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 3. Timetable Type */}
      <div className='step1-basics-section' style={sectionStyle}>
        <div className='step1-basics-info'>
          <div className='d-flex align-items-start gap-3'>
            <div style={iconBubbleStyle}>
              <Users size={20} />
            </div>
            <div>
              <h6 style={sectionTitleStyle}>
                3. {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.TYPE_TITLE'})}
              </h6>
              <button
                type='button'
                className='step1-viewall-link'
                style={{marginBottom: '0.4rem'}}
                onClick={() => setModeHelpOpen((open) => !open)}
              >
                <Info size={13} />
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.TYPE_WHICH_OPTION'})}
              </button>
              <p style={sectionDescStyle}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.TYPE_DESC'})}
              </p>
            </div>
          </div>
        </div>
        <div className='step1-basics-body'>
          {modeHelpOpen && (
            <div
              className='mb-3 p-3'
              style={{
                borderRadius: 10,
                border: `1px solid ${isDark ? '#2b2b40' : '#e4e6ef'}`,
                backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#f9f9fb',
              }}
            >
              <p style={{...mutedNoteStyle, marginBottom: '0.5rem'}}>
                <strong style={{color: isDark ? '#d1d3e0' : '#3f4254'}}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CLASS_WISE_TITLE'})}:
                </strong>{' '}
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CLASS_WISE_HINT'})}
              </p>
              <p style={{...mutedNoteStyle, marginBottom: 0}}>
                <strong style={{color: isDark ? '#d1d3e0' : '#3f4254'}}>
                  {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.SUBJECT_WISE_TITLE'})}:
                </strong>{' '}
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.SUBJECT_WISE_HINT'})}
              </p>
            </div>
          )}
          <div className='d-flex flex-column flex-md-row gap-3'>
            <div
              className='onboarding-step-card'
              style={modeCardStyle('class')}
              onClick={() => updateData({mode: 'class'})}
            >
              <span className='onboarding-mode-recommended-badge'>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CLASS_WISE_RECOMMENDED'})}
              </span>
              <div className='mb-2' style={{marginTop: '0.75rem'}}>
                <i
                  className='fas fa-chalkboard-teacher'
                  style={{fontSize: '1.8rem', color: isDark ? '#d1d3e0' : '#5E6278'}}
                />
              </div>
              <h6
                className='fw-bold mb-1'
                style={{color: isDark ? '#ffffff' : '#181C32', fontSize: '1rem'}}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CLASS_WISE_TITLE'})}
              </h6>
              <p className='mb-0' style={{...sectionDescStyle, fontSize: '0.85rem'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.CLASS_WISE_DESC'})}
              </p>
            </div>

            <div
              className='onboarding-step-card'
              style={modeCardStyle('subject')}
              onClick={() => updateData({mode: 'subject'})}
            >
              <div className='mb-2' style={{marginTop: '0.75rem'}}>
                <i
                  className='fas fa-book-open'
                  style={{fontSize: '1.8rem', color: isDark ? '#d1d3e0' : '#5E6278'}}
                />
              </div>
              <h6
                className='fw-bold mb-1'
                style={{color: isDark ? '#ffffff' : '#181C32', fontSize: '1rem'}}
              >
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.SUBJECT_WISE_TITLE'})}
              </h6>
              <p className='mb-0' style={{...sectionDescStyle, fontSize: '0.85rem'}}>
                {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP1.SUBJECT_WISE_DESC'})}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className='d-flex justify-content-end mt-2'>
        <button
          type='button'
          className='onboarding-btn-next'
          onClick={onNext}
          disabled={isNextDisabled}
        >
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.NEXT'})}{' '}
          <ArrowRight size={16} className='ms-2' />
        </button>
      </div>
    </div>
  )
}

export default Step1Mode
